import { Injectable } from '@angular/core';
import {
  DwAction,
  DwActionHandler,
  DwActionResult,
  DwActionType,
  DwMdFormActionState,
  DwModalButtonStandardConfigs,
  DwModalConfig,
  DwModalResult,
  DwModalService,
  DwModalSize
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AppMetaDataItemNames, FlightSchoolExaminerEntity } from '../../../meta-data/app-meta-data.service';
import { UnitMemberPdfOptionsModalComponent } from '../components/unit-member-pdf-options-modal/unit-member-pdf-options-modal.component';
import {
  UnitMemberPdfOptionsModalData,
  UnitMemberPdfOptionsModalResult,
  UnitMemberPdfSectionOption
} from '../models/unit-member-pdf.model';
import { UnitMemberPdfService } from '../services/unit-member-pdf.service';

@DwAction({
  keyName: 'unit-member-generate-pdf-action',
  display: 'Generate Unit Member PDF',
  actionType: DwActionType.formAction,
  isGlobal: false,
  parentItemName: [AppMetaDataItemNames.FlightSchoolExaminer],
  allowOnce: true,
  defaultButtonConfig: {
    useButtonDefaults: false,
    useParentDisplay: false,
    buttonText: 'Generate PDF',
    buttonClass: 'btn btn-secondary mr-1 mt-1',
    iconClass: 'fa fa-file-pdf-o'
  },
  configData: {
    requireValidation: false,
    redirectAfter: false,
    actionMessage: 'Generating PDF...'
  }
})
@Injectable()
export class UnitMemberGeneratePdfActionService implements DwActionHandler {
  constructor(private modalService: DwModalService, private pdfService: UnitMemberPdfService) { }

  handleAction(actionState: DwMdFormActionState): Observable<DwActionResult> {
    const member = actionState.formApi.getFormData<FlightSchoolExaminerEntity>();
    if (!member.FlightSchoolExaminerId) {
      return of(this.result(false, true));
    }

    return this.pdfService.getOptions(member.FlightSchoolExaminerId).pipe(
      mergeMap(sections => this.showOptions(sections)),
      mergeMap(options => {
        if (!options) return of(this.result(false, true));
        return this.pdfService.download(member.FlightSchoolExaminerId, options.sectionCodes)
          .pipe(map(() => this.result(true, false)));
      })
    );
  }

  private showOptions(sections: UnitMemberPdfSectionOption[]): Observable<UnitMemberPdfOptionsModalResult> {
    const config = new DwModalConfig<UnitMemberPdfOptionsModalData>();
    config.buttons = DwModalButtonStandardConfigs.instance.okAndClose;
    config.allowFullScreen = false;
    config.modalSize = DwModalSize.medium;
    config.component = UnitMemberPdfOptionsModalComponent;
    config.title = 'Generate Unit Member PDF';
    config.data = { sections };
    return this.modalService.showModal<UnitMemberPdfOptionsModalData, UnitMemberPdfOptionsModalResult>(config)
      .pipe(map((result: DwModalResult<UnitMemberPdfOptionsModalResult>) => result.cancelled ? null : result.data));
  }

  private result(success: boolean, cancelled: boolean): DwActionResult {
    const result = new DwActionResult();
    result.success = success;
    result.cancelled = cancelled;
    return result;
  }
}
